import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CalendarDaysIcon, ChevronLeftIcon, XIcon } from 'lucide-react';
import { CALENDLY_URL, timeSlots } from '../data/site';
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const CAMILA_PHOTO = 'https://cdn.magicpatterns.com/patterns/generated-images/00318369-f44c-4291-b3ae-b040906248df.jpg';
type MonthOption = {
  key: string;
  label: string;
  month: number;
  year: number;
};
const ease = [0.23, 1, 0.32, 1] as const;
function buildMonths(from: Date): MonthOption[] {
  return Array.from({
    length: 12
  }).map((_, i) => {
    const date = new Date(from.getFullYear(), from.getMonth() + i, 1);
    const month = date.getMonth();
    const year = date.getFullYear();
    return {
      key: `${year}-${month}`,
      label: MONTH_NAMES[month],
      month,
      year
    };
  });
}

/* Índice de la columna (lunes = 0) en la que arranca el mes */
function startOffset(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return (day + 6) % 7;
}
export function BookingCalendar() {
  const [open, setOpen] = useState(false);
  const [monthKey, setMonthKey] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);
  const months = useMemo(() => buildMonths(today), [today]);
  const activeMonth = months.find(m => m.key === monthKey) ?? null;
  const days = useMemo(() => {
    if (!activeMonth) return [];
    const total = new Date(activeMonth.year, activeMonth.month + 1, 0).getDate();
    return Array.from({
      length: total
    }).map((_, i) => {
      const dayNumber = i + 1;
      const date = new Date(activeMonth.year, activeMonth.month, dayNumber);
      const isSunday = date.getDay() === 0;
      const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return {
        dayNumber,
        disabled: isSunday || isPast
      };
    });
  }, [activeMonth, today]);
  const reset = () => {
    setOpen(false);
    setMonthKey(null);
    setSelectedDay(null);
    setSelectedTime(null);
  };
  const selectionLabel = activeMonth && selectedDay ? `${selectedDay} de ${activeMonth.label}${selectedTime ? ` · ${selectedTime} h` : ''}` : null;
  return <div className="overflow-hidden rounded-3xl border border-blush-200 bg-cream shadow-soft" data-id="element-66" data-mp="21ia5ms0u7rhh">
      <AnimatePresence mode="wait" initial={false} data-id="element-67" data-mp="1nmpx8qiqirob">
        {!open ? <motion.div key="portada" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-68" data-mp="1skhodibk29rg">
            <button type="button" onClick={() => setOpen(true)} className="group relative block w-full text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep" data-id="element-69" data-mp="3mwy1n32rqo5t">
              <img src={CAMILA_PHOTO} alt="Camila, nail artist de Cami Nails, lista para atenderte en el estudio" className="aspect-[4/5] w-full object-cover transition-transform duration-300 ease-smooth group-hover:scale-[1.03] sm:aspect-[5/4] lg:aspect-[4/5]" data-id="element-70" data-mp="1l0yvmkcif25o" />
              <span className="absolute inset-x-0 bottom-0 flex flex-col gap-4 bg-ink/55 p-6 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:p-7" data-id="element-71" data-mp="1p3v95wn7082r">
                <span className="block" data-id="element-72" data-mp="1z19r60orl4x6">
                  <span className="block font-serif text-2xl font-semibold text-white sm:text-3xl" data-id="element-73" data-mp="1yy438orvg931">
                    Elegí tu día y horario
                  </span>
                  <span className="mt-1 block text-sm text-blush-100" data-id="element-74" data-mp="4nia4g43virp">
                    Turnos de lunes a sábado con Camila
                  </span>
                </span>
                <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-fucsia-deep px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-150 ease-smooth group-hover:-translate-y-0.5 sm:self-auto" data-id="element-75" data-mp="2wser9meq6qkr">
                  <CalendarDaysIcon className="h-4 w-4" aria-hidden="true" data-id="element-76" data-mp="35s1qq2nw9mb6" />
                  Ver agenda
                </span>
              </span>
            </button>
          </motion.div> : <motion.div key="agenda" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.25,
        ease
      }} className="p-6 sm:p-7" data-id="element-77" data-mp="2e0lwbmg3ivae">
            <div className="flex items-start justify-between gap-4" data-id="element-78" data-mp="1qcvyahv7rq57">
              <div className="flex items-center gap-3" data-id="element-79" data-mp="1ue7k0cayvr84">
                <img src={CAMILA_PHOTO} alt="" className="h-12 w-12 rounded-full object-cover" data-id="element-80" data-mp="1ucmmqex9pu6k" />
                <div data-id="element-81" data-mp="1hwwn9ly0571s">
                  <p className="font-serif text-xl font-semibold text-ink" data-id="element-82" data-mp="1hual61tvrjqy">
                    {activeMonth ? `Turnos de ${activeMonth.label}` : 'Elegí el mes'}
                  </p>
                  <p className="text-sm text-ink-soft" data-id="element-83" data-mp="18vfc8hzgbvjy">
                    {activeMonth ? 'Seleccioná el día y la hora que te queden mejor' : 'Agenda abierta los próximos 12 meses'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={reset} aria-label="Cerrar agenda" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blush-200 bg-white text-ink-soft transition-colors duration-150 ease-smooth hover:bg-blush-100 hover:text-ink" data-id="element-84" data-mp="u7c3j0u0y5rg">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-85" data-mp="6j5tdw54wj3r" />
              </button>
            </div>

            {!activeMonth ? <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-86" data-mp="bk379wj42qzg">
                {months.map(month => <li key={month.key} data-id="element-87" data-mp="3ssq6kis3ljz4">
                    <button type="button" onClick={() => {
              setMonthKey(month.key);
              setSelectedDay(null);
              setSelectedTime(null);
            }} className="w-full rounded-2xl border border-blush-200 bg-white px-4 py-4 text-left transition-[transform,border-color,background-color] duration-150 ease-smooth hover:-translate-y-0.5 hover:border-fucsia-deep/40 hover:bg-blush-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep" data-id="element-88" data-mp="1jsyqp80u7qm">
                      <span className="block font-semibold text-ink" data-id="element-89" data-mp="1d1kicjgxny54">{month.label}</span>
                      <span className="mt-0.5 block text-xs text-ink-soft" data-id="element-90" data-mp="2xo24s4nddsoh">{month.year}</span>
                    </button>
                  </li>)}
              </ul> : <div className="mt-6" data-id="element-91" data-mp="37ewpv5ovqm8c">
                <button type="button" onClick={() => {
            setMonthKey(null);
            setSelectedDay(null);
            setSelectedTime(null);
          }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-fucsia-deep transition-colors duration-150 ease-smooth hover:text-fucsia-ink" data-id="element-92" data-mp="32gv7yo36wsyg">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-93" data-mp="2jpvp84magfdm" />
                  Cambiar de mes
                </button>

                <div className="mt-5 grid gap-6 sm:grid-cols-[1.15fr_0.85fr] sm:gap-7" data-id="element-94" data-mp="2zwyfnjq8vlr">
                  <div data-id="element-95" data-mp="3v4ipfu95z3gv">
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-96" data-mp="3v7d9vls6goaa">
                      {WEEKDAY_LABELS.map((label, i) => <span key={`${label}-${i}`} aria-hidden="true" className="pb-2 text-xs font-semibold uppercase text-ink-soft" data-id="element-97" data-mp="1xhgh0oiuwsh7" data-mpi={i}>
                          {label}
                        </span>)}
                      {Array.from({
                  length: startOffset(activeMonth.year, activeMonth.month)
                }).map((_, i) => <span key={`vacio-${i}`} data-id="element-98" data-mp="2l7asb1nj28zc" data-mpi={i} />)}
                      {days.map(day => {
                  const isSelected = selectedDay === day.dayNumber;
                  return <button key={day.dayNumber} type="button" disabled={day.disabled} aria-pressed={isSelected} onClick={() => {
                    setSelectedDay(day.dayNumber);
                    setSelectedTime(null);
                  }} className={`aspect-square rounded-xl text-sm font-medium transition-[background-color,color,transform] duration-150 ease-smooth focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep ${day.disabled ? 'cursor-not-allowed text-ink-soft/35' : isSelected ? 'bg-fucsia-deep text-white' : 'bg-white text-ink hover:bg-blush-100'}`} data-id="element-99" data-mp="qu1if8r2klgk">
                            {day.dayNumber}
                          </button>;
                })}
                    </div>
                    <p className="mt-4 text-xs text-ink-soft" data-id="element-100" data-mp="28zsfhp93uhg7">
                      Domingos cerrado. Los horarios se sincronizan con la agenda de Calendly.
                    </p>
                  </div>

                  <div className="border-t border-blush-200 pt-5 sm:border-l sm:border-t-0 sm:pl-7 sm:pt-0" data-id="element-101" data-mp="2irgag04vegmf">
                    <p className="text-sm font-semibold uppercase tracking-wider text-ink-soft" data-id="element-102" data-mp="2dywly84na12u">
                      Horarios
                    </p>
                    {selectedDay ? <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-1" data-id="element-103" data-mp="2mqyl832z9bbj">
                        {timeSlots.map(slot => {
                  const isSelected = selectedTime === slot;
                  return <li key={slot} data-id="element-104" data-mp="2wo35tyh6cyq8">
                              <button type="button" aria-pressed={isSelected} onClick={() => setSelectedTime(slot)} className={`w-full rounded-xl border px-4 py-3 text-sm font-semibold transition-[background-color,border-color,color] duration-150 ease-smooth focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep ${isSelected ? 'border-fucsia-deep bg-fucsia-deep text-white' : 'border-blush-200 bg-white text-ink hover:border-fucsia-deep/40 hover:bg-blush-50'}`} data-id="element-105" data-mp="17ag8sdwmupfq">
                                {slot} h
                              </button>
                            </li>;
                })}
                      </ul> : <p className="mt-4 text-sm leading-relaxed text-ink-soft" data-id="element-106" data-mp="jjzn055oka5z">
                        Elegí un día del calendario para ver los horarios disponibles.
                      </p>}
                  </div>
                </div>

                <div className="mt-7 flex flex-col gap-3 border-t border-blush-200 pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-107" data-mp="i217u81h2me5">
                  <p className="text-sm text-ink-soft" data-id="element-108" data-mp="d3puhnbyxtr6">
                    {selectionLabel ? <>
                        Turno elegido:{' '}
                        <span className="font-semibold text-ink" data-id="element-109" data-mp="15qtv7g413lx5">{selectionLabel}</span>
                      </> : 'Todavía no elegiste día y hora.'}
                  </p>
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" aria-disabled={!selectedTime} onClick={event => {
              if (!selectedTime) event.preventDefault();
            }} className={`group inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-[transform,background-color] duration-150 ease-smooth ${selectedTime ? 'bg-fucsia-deep text-white hover:-translate-y-0.5 hover:bg-fucsia-ink' : 'pointer-events-none bg-blush-200 text-ink-soft'}`} data-id="element-110" data-mp="w092g9b1dbm5">
                    Continuar y pagar la seña
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-111" data-mp="2f0qd4eyw6ab5" />
                  </a>
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}